import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Button } from "./ui/button";
import { useCart } from "../context/CartContext";
import { useLang } from "../context/LanguageContext";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";

export default function CartSheet({ onCheckout, showPrices = true }) {
  const { items, updateQty, remove, total, open, setOpen } = useCart();
  const { t } = useLang();

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        side="right"
        className="carta-scope w-full sm:max-w-md bg-card border-l border-border flex flex-col p-0 text-foreground"
        data-testid="cart-drawer"
      >
        <SheetHeader className="p-6 border-b border-border">
          <SheetTitle className="font-serif text-3xl font-light text-foreground">
            {t("cart_title")}
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground gap-3 px-6">
            <ShoppingBag className="w-12 h-12 opacity-40" strokeWidth={1} />
            <p className="text-sm">{t("cart_empty")}</p>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5" data-testid="cart-items">
            {items.map((i) => (
              <div
                key={i.key}
                className="flex items-start justify-between gap-4"
                data-testid={`cart-line-${i.item_id}`}
              >
                <div className="flex-1">
                  <p className="text-foreground font-medium leading-tight">{i.name}</p>
                  {i.variant && (
                    <p className="text-muted-foreground text-xs mt-0.5">{i.variant}</p>
                  )}
                  {showPrices && (
                    <p className="text-primary text-sm mt-1">{i.price.toFixed(2)} €</p>
                  )}
                  <div className="flex items-center gap-3 mt-2">
                    <button
                      onClick={() => updateQty(i.key, i.quantity - 1)}
                      className="w-7 h-7 flex items-center justify-center border border-border rounded-sm hover:border-primary transition-colors"
                      data-testid={`cart-decrease-${i.item_id}`}
                    >
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                    <span
                      className="w-6 text-center text-sm"
                      data-testid={`cart-qty-${i.item_id}`}
                    >
                      {i.quantity}
                    </span>
                    <button
                      onClick={() => updateQty(i.key, i.quantity + 1)}
                      className="w-7 h-7 flex items-center justify-center border border-border rounded-sm hover:border-primary transition-colors"
                      data-testid={`cart-increase-${i.item_id}`}
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                <div className="text-right">
                  {showPrices && (
                    <p className="text-foreground font-medium">
                      {(i.price * i.quantity).toFixed(2)} €
                    </p>
                  )}
                  <button
                    onClick={() => remove(i.key)}
                    className="text-muted-foreground hover:text-destructive transition-colors mt-2"
                    data-testid={`cart-remove-${i.item_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {items.length > 0 && (
          <div className="border-t border-border p-6 space-y-4">
            {showPrices && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground uppercase tracking-widest text-xs">
                  {t("total")}
                </span>
                <span
                  className="font-serif text-3xl text-primary"
                  data-testid="cart-total"
                >
                  {total.toFixed(2)} €
                </span>
              </div>
            )}
            <Button
              className="w-full rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 h-12 tracking-widest text-xs uppercase"
              onClick={() => {
                setOpen(false);
                onCheckout();
              }}
              data-testid="checkout-btn"
            >
              {t("place_order")}
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
